import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calculator } from 'lucide-react';
import { WeibullDataPoint, WeibullAnalysis } from '../types/analytics.types';
import { calculateWeibullAnalysis } from '../utils/weibullMath';
import { WeibullResults } from '../components/weibull/WeibullResults';

const WeibullTool: React.FC = () => {
    const [dataPoints, setDataPoints] = useState<WeibullDataPoint[]>([]);
    const [analysis, setAnalysis] = useState<Partial<WeibullAnalysis> | null>(null);
    const [title, setTitle] = useState('');
    const [productName, setProductName] = useState('');
    const [testCondition, setTestCondition] = useState('');
    const [confidenceLevel, setConfidenceLevel] = useState(0.90);
    const [calculating, setCalculating] = useState(false);

    const addDataPoint = () => {
        const newPoint: WeibullDataPoint = {
            id: `dp-${Date.now()}`,
            failureTime: 0,
            quantity: 1,
            censoringStatus: 'Failed',
        };
        setDataPoints([...dataPoints, newPoint]);
    };

    const updateDataPoint = (id: string, field: keyof WeibullDataPoint, value: number | string) => {
        setDataPoints(points =>
            points.map(p => (p.id === id ? { ...p, [field]: value } : p))
        );
    };

    const removeDataPoint = (id: string) => {
        setDataPoints(points => points.filter(p => p.id !== id));
    };

    const handleCalculate = () => {
        if (dataPoints.length < 3) {
            alert('Need at least 3 data points for Weibull analysis');
            return;
        }

        setCalculating(true);
        setTimeout(() => {
            const result = calculateWeibullAnalysis(
                dataPoints,
                title,
                productName,
                testCondition,
                confidenceLevel
            );
            setAnalysis(result);
            setCalculating(false);
        }, 500);
    };

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <div className="mb-6">
                <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Analytics
                </Link>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Weibull Analysis</h1>
                        <p className="text-gray-600 mt-1">B10/B50 Life Prediction & Reliability Modeling</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Analysis Information</h2>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Title *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Pump Bearing Life Test"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                        <input
                            type="text"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Model XYZ Pump"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test Condition</label>
                        <input
                            type="text"
                            value={testCondition}
                            onChange={(e) => setTestCondition(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., 85°C, 100% Load"
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Level</label>
                    <select
                        value={confidenceLevel}
                        onChange={(e) => setConfidenceLevel(parseFloat(e.target.value))}
                        className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value={0.90}>90%</option>
                        <option value={0.95}>95%</option>
                        <option value={0.99}>99%</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Failure Data</h2>
                    <button
                        onClick={addDataPoint}
                        className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Data Point
                    </button>
                </div>
                {dataPoints.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Failure Time (hours)</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {dataPoints.map((point, index) => (
                                    <tr key={point.id}>
                                        <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                value={point.failureTime}
                                                onChange={(e) => updateDataPoint(point.id, 'failureTime', parseFloat(e.target.value))}
                                                className="w-32 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="0"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                value={point.quantity}
                                                onChange={(e) => updateDataPoint(point.id, 'quantity', parseInt(e.target.value))}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="1"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={point.censoringStatus}
                                                onChange={(e) => updateDataPoint(point.id, 'censoringStatus', e.target.value)}
                                                className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="Failed">Failed</option>
                                                <option value="Censored">Censored</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => removeDataPoint(point.id)} className="text-red-600 hover:text-red-700">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                        <p className="text-gray-600 mb-4">No failure data entered yet</p>
                        <button onClick={addDataPoint} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            Add First Data Point
                        </button>
                    </div>
                )}
                {dataPoints.length > 0 && (
                    <div className="mt-6 flex justify-center">
                        <button
                            onClick={handleCalculate}
                            disabled={calculating || dataPoints.length < 3}
                            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Calculator className="w-5 h-5" />
                            {calculating ? 'Calculating...' : 'Calculate Weibull Parameters'}
                        </button>
                    </div>
                )}
            </div>

            {analysis && <WeibullResults analysis={analysis} />}
        </div>
    );
};

export default WeibullTool;
